/**
 * Simulador en modo chat manual — pantalla de testeo interactivo del bot.
 *
 * El operador escribe un mensaje, la IA responde, y por cada respuesta puede:
 *  - Validar (✓): la respuesta queda como buena, no pasa nada más.
 *  - Rechazar (✗): se abre un panel para cargar la respuesta correcta. Cuando
 *    se guarda, se crea una nueva versión del manual del cliente con el caso
 *    agregado a la sección "Casos validados" (ver routes/skills.ts).
 *
 * Convive con `/sandbox` (batch CSV) — uno sirve para regression, este para
 * iteración rápida y feedback loop hacia el manual.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  Check,
  ChevronLeft,
  MessageSquare,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useSimulateModeration, type ModerationDecision } from "@/hooks/use-simulate";
import {
  useSkillsForClient,
  useAppendValidatedCase,
} from "@/hooks/use-skills";
import {
  useSimulatorSessions,
  useSimulatorSession,
  useCreateSimulatorSession,
  useUpdateSimulatorSession,
  useDeleteSimulatorSession,
} from "@/hooks/use-simulator-sessions";
import { findClient } from "@/lib/clients";
import { EmptyState } from "@/components/shared/empty-state";
import { cn } from "@/lib/cn";
import type { ClientSlug, Platform } from "@/types";

interface ChatTurn {
  id: string;
  userMessage: string;
  decision: ModerationDecision | null;
  status: "pending" | "ready" | "approved" | "rejected" | "fixed";
  /** Si se cargó la respuesta correcta, queda guardada acá para no perderla. */
  correctReply?: string;
}

export function SandboxChatPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const client = findClient(clientSlug);
  const [platform, setPlatform] = useState<Platform>("instagram");
  const [kind, setKind] = useState<"dm" | "comment">("dm");
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);

  // Persistencia: sesión activa (id real de la DB cuando ya se guardó).
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  // Tracking de sesiones recién creadas localmente — no las re-hidratamos del
  // server porque el primer turno ya vive en memoria y el server arranca vacío.
  const justCreatedRef = useRef<string | null>(null);

  const sessions = useSimulatorSessions(client?.slug);
  const sessionDetail = useSimulatorSession(activeSessionId);
  const createSession = useCreateSimulatorSession();
  const updateSession = useUpdateSimulatorSession();
  const deleteSession = useDeleteSimulatorSession();

  // Al cargar una sesión existente, hidratamos los turnos en memoria.
  // Excepción: si la sesión es una que acabamos de crear en esta pestaña, los
  // turnos locales son la fuente de verdad — el server aún no recibió el primer
  // turno (que está pendiente del auto-save).
  useEffect(() => {
    if (!sessionDetail.data) return;
    const s = sessionDetail.data;
    if (s.id === justCreatedRef.current) return;
    setTurns((s.turns as ChatTurn[]) ?? []);
    setPlatform(s.platform as Platform);
    setKind(s.kind as "dm" | "comment");
  }, [sessionDetail.data?.id]);

  // Auto-save con debounce: cada cambio en turns se persiste tras 1s.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!activeSessionId || turns.length === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateSession.mutate({ id: activeSessionId, turns });
    }, 1000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [turns, activeSessionId]);

  function startNewSession() {
    justCreatedRef.current = null;
    setActiveSessionId(null);
    setTurns([]);
    setInput("");
  }

  function switchToSession(id: string) {
    justCreatedRef.current = null;
    setActiveSessionId(id);
  }

  const simulate = useSimulateModeration();

  if (!client) {
    return (
      <div className="p-7 min-h-full bg-background">
        <EmptyState
          title="Seleccioná un cliente"
          description="El simulador necesita un cliente para cargar su manual."
        />
      </div>
    );
  }

  async function handleSend() {
    if (!input.trim() || simulate.isPending) return;
    const turnId = crypto.randomUUID();
    const message = input.trim();
    setInput("");

    const isFirstTurn = turns.length === 0 && !activeSessionId;
    setTurns((prev) => [
      ...prev,
      { id: turnId, userMessage: message, decision: null, status: "pending" },
    ]);

    // Si es el primer mensaje y todavía no hay sesión, la creamos.
    // El título arranca como un recorte del primer mensaje; el operador puede
    // renombrarla luego desde el sidebar.
    if (isFirstTurn && client) {
      const title = message.slice(0, 80);
      createSession.mutate(
        {
          clientSlug: client.slug as ClientSlug,
          title,
          platform,
          kind,
          turns: [],
        },
        {
          onSuccess: (s) => {
            justCreatedRef.current = s.id;
            setActiveSessionId(s.id);
          },
        },
      );
    }

    // Construimos el historial para que el bot mantenga continuidad: cada
    // turno previo aporta el mensaje del usuario + la respuesta del bot que
    // efectivamente "respondió" (escaladas/ignores no son parte del diálogo).
    // Si el operador corrigió la respuesta (status=fixed), pasamos la corregida
    // — así el bot aprende dentro de la misma sesión.
    const history: { role: "user" | "assistant"; content: string }[] = [];
    for (const t of turns) {
      if (!t.decision) continue;
      const assistantText =
        t.status === "fixed" && t.correctReply
          ? t.correctReply
          : t.decision.action === "respond"
            ? t.decision.response
            : null;
      if (!assistantText) continue;
      history.push({ role: "user", content: t.userMessage });
      history.push({ role: "assistant", content: assistantText });
    }

    simulate.mutate(
      {
        clientSlug: client!.slug as ClientSlug,
        platform,
        kind,
        message,
        history: history.length > 0 ? history : undefined,
      },
      {
        onSuccess: (decision) => {
          setTurns((prev) =>
            prev.map((t) =>
              t.id === turnId ? { ...t, decision, status: "ready" } : t,
            ),
          );
        },
        onError: () => {
          setTurns((prev) =>
            prev.map((t) =>
              t.id === turnId ? { ...t, status: "rejected" } : t,
            ),
          );
        },
      },
    );
  }

  function updateTurn(id: string, patch: Partial<ChatTurn>) {
    setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }

  return (
    <div className="h-full bg-background flex overflow-hidden">
      {/* Sidebar de sesiones */}
      <aside className="w-72 shrink-0 border-r border-subtle bg-surface-1 flex flex-col hidden md:flex">
          <div className="px-4 py-3 border-b border-subtle flex items-center justify-between">
            <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-text-muted">
              Sesiones
            </h2>
            <button
              type="button"
              onClick={startNewSession}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-2xs font-medium transition-colors"
            >
              <Plus size={11} strokeWidth={2} />
              Nueva
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.isLoading ? (
              <li className="font-sans text-xs text-text-muted px-2">Cargando…</li>
            ) : !sessions.data?.length ? (
              <li className="font-sans text-xs text-text-muted px-2 py-3">
                Sin sesiones todavía. Escribí un mensaje para arrancar una.
              </li>
            ) : (
              sessions.data.map((s) => (
                <SessionItem
                  key={s.id}
                  session={s}
                  isActive={activeSessionId === s.id}
                  onActivate={() => switchToSession(s.id)}
                  onDelete={() => {
                    if (!confirm("¿Eliminar esta sesión?")) return;
                    deleteSession.mutate({ id: s.id, clientSlug: client!.slug as ClientSlug });
                    if (activeSessionId === s.id) startNewSession();
                  }}
                  onRename={(title) =>
                    updateSession.mutate({ id: s.id, title })
                  }
                />
              ))
            )}
          </ul>
        </aside>

      {/* Panel principal: chat */}
      <div className="flex-1 flex flex-col overflow-hidden">
      <header className="px-7 pt-5 pb-4 border-b border-subtle bg-surface-1 shrink-0">
        <Link
          to="/c/$clientSlug"
          params={{ clientSlug: client.slug }}
          className="inline-flex items-center gap-1.5 font-sans text-xs text-text-muted hover:text-text-primary mb-3"
        >
          <ChevronLeft size={12} strokeWidth={1.75} />
          {client.fullName}
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-sans text-2xl font-semibold tracking-tight text-text-primary">
              Simulador interactivo
            </h1>
            <p className="font-sans text-sm text-text-muted mt-1">
              Probá el bot manualmente. Validá lo que responde bien, corregí lo que no.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="h-8 px-2 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as "dm" | "comment")}
              className="h-8 px-2 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
            >
              <option value="dm">DM</option>
              <option value="comment">Comentario</option>
            </select>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-7 py-6 min-h-0">
        <div className="max-w-3xl mx-auto space-y-4">
          {turns.length === 0 ? (
            <EmptyState
              icon={<MessageSquare size={28} strokeWidth={1.5} />}
              title="Escribí un mensaje para empezar"
              description="Probá cosas que un usuario real podría preguntar: dudas de producto, quejas, mensajes ambiguos. La IA responde según el manual del cliente, y vos validás o corregís."
            />
          ) : (
            turns.map((turn) => (
              <ChatTurnCard
                key={turn.id}
                turn={turn}
                clientSlug={client.slug as ClientSlug}
                onUpdate={(patch) => updateTurn(turn.id, patch)}
              />
            ))
          )}
        </div>
      </main>

      <footer className="border-t border-subtle bg-background px-7 py-4 shrink-0">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl border border-default bg-surface-1 p-3 shadow-sm focus-within:border-magenta focus-within:shadow-focus transition-colors">
            <textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter envía, Shift+Enter inserta nueva línea (convención chat).
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Mensaje del usuario simulado…"
              className="w-full resize-none bg-transparent border-0 outline-none font-sans text-[15px] leading-6 text-text-primary placeholder:text-text-muted min-h-[60px] max-h-32"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="font-sans text-xs text-text-muted">
                Enter para enviar · Shift+Enter para nueva línea
              </span>
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || simulate.isPending}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-magenta text-white font-sans text-sm font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={14} strokeWidth={2} />
                {simulate.isPending ? "Generando…" : "Enviar al bot"}
              </button>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

function ChatTurnCard({
  turn,
  clientSlug,
  onUpdate,
}: {
  turn: ChatTurn;
  clientSlug: ClientSlug;
  onUpdate: (patch: Partial<ChatTurn>) => void;
}) {
  return (
    <div className="space-y-2">
      {/* Mensaje del usuario */}
      <div className="flex justify-start">
        <div className="max-w-[78%] rounded-2xl rounded-bl-md border border-default bg-surface-2 px-[18px] py-[14px] font-sans text-[15px] leading-6 text-text-primary">
          {turn.userMessage}
        </div>
      </div>

      {/* Respuesta del bot */}
      <div className="flex justify-end">
        <div className="max-w-[78%]">
          {turn.status === "pending" ? (
            <div className="rounded-2xl rounded-br-md border border-default bg-surface-3 px-[18px] py-[14px] font-sans text-sm text-text-muted">
              Generando…
            </div>
          ) : turn.decision ? (
            <BotReplyBubble decision={turn.decision} />
          ) : (
            <div className="rounded-2xl border border-error-border bg-error-bg px-[18px] py-[14px] font-sans text-sm text-error-text">
              Error generando respuesta.
            </div>
          )}
        </div>
      </div>

      {/* Acciones de validación */}
      {turn.decision && turn.status === "ready" && (
        <ValidationBar
          onApprove={() => onUpdate({ status: "approved" })}
          onReject={() => onUpdate({ status: "rejected" })}
        />
      )}

      {turn.status === "approved" && (
        <p className="text-center font-sans text-xs text-success-text">
          ✓ Respuesta validada
        </p>
      )}

      {turn.status === "rejected" && (
        <FixReplyForm
          turn={turn}
          clientSlug={clientSlug}
          onFixed={(correctReply) => onUpdate({ status: "fixed", correctReply })}
          onCancel={() => onUpdate({ status: "ready" })}
        />
      )}

      {turn.status === "fixed" && turn.correctReply && (
        <div className="rounded-xl border border-success-border bg-success-bg px-4 py-3">
          <div className="font-sans text-xs font-semibold text-success-text mb-1">
            ✓ Caso agregado al manual
          </div>
          <p className="font-sans text-sm text-success-text">
            Respuesta correcta: {turn.correctReply}
          </p>
        </div>
      )}
    </div>
  );
}

function BotReplyBubble({ decision }: { decision: ModerationDecision }) {
  if (decision.action === "respond") {
    return (
      <div className="rounded-2xl rounded-br-md border border-default bg-surface-3 px-[18px] py-[14px] font-sans text-[15px] leading-6 text-text-primary">
        <div className="text-xs mb-2 flex items-center gap-1.5 text-text-muted">
          <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-semibold">
            AI
          </span>
          <span>·</span>
          <span>respondería</span>
        </div>
        {decision.response}
      </div>
    );
  }
  if (decision.action === "escalate") {
    return (
      <div className="rounded-2xl border border-state-escalated-border bg-state-escalated-bg px-[18px] py-[14px]">
        <div className="text-xs mb-2 flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full border border-state-escalated-border bg-state-escalated-bg px-2 py-0.5 text-2xs font-sans font-semibold text-state-escalated-text">
            ESCALARÍA
          </span>
          <span className="text-text-muted">·</span>
          <span className="font-mono text-2xs text-text-muted">
            categoría {decision.category}
          </span>
        </div>
        <p className="font-sans text-sm text-state-escalated-text">
          {decision.reason}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-default bg-surface-2 px-[18px] py-[14px]">
      <div className="text-xs mb-2 flex items-center gap-1.5">
        <span className="inline-flex items-center rounded-full border border-default bg-surface-3 px-2 py-0.5 text-2xs font-sans font-semibold text-text-muted">
          IGNORARÍA
        </span>
      </div>
      <p className="font-sans text-sm text-text-muted">{decision.reason}</p>
    </div>
  );
}

function ValidationBar({
  onApprove,
  onReject,
}: {
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      <button
        type="button"
        onClick={onApprove}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-success-border bg-success-bg text-success-text hover:opacity-90 transition-colors font-sans text-xs font-medium"
      >
        <Check size={12} strokeWidth={2} />
        Validar
      </button>
      <button
        type="button"
        onClick={onReject}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-error-border bg-error-bg text-error-text hover:opacity-90 transition-colors font-sans text-xs font-medium"
      >
        <X size={12} strokeWidth={2} />
        Rechazar y corregir
      </button>
    </div>
  );
}

function FixReplyForm({
  turn,
  clientSlug,
  onFixed,
  onCancel,
}: {
  turn: ChatTurn;
  clientSlug: ClientSlug;
  onFixed: (correctReply: string) => void;
  onCancel: () => void;
}) {
  const { data: skills } = useSkillsForClient(clientSlug);
  const append = useAppendValidatedCase();
  const [correctReply, setCorrectReply] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedSkillId, setSelectedSkillId] = useState<string>(
    skills?.[0]?.id ?? "",
  );

  // Sincronizar default cuando skills cargan (en useEffect para evitar el
  // warning "Cannot update a component while rendering").
  useEffect(() => {
    if (skills && skills.length > 0 && !selectedSkillId) {
      setSelectedSkillId(skills[0].id);
    }
  }, [skills, selectedSkillId]);

  const rejectedReply =
    turn.decision?.action === "respond"
      ? turn.decision.response
      : turn.decision?.action === "escalate"
        ? `[escalación] ${turn.decision.reason}`
        : `[ignorar] ${turn.decision?.reason ?? ""}`;

  function handleSave() {
    if (!correctReply.trim() || !selectedSkillId) return;
    append.mutate(
      {
        skillId: selectedSkillId,
        userMessage: turn.userMessage,
        rejectedReply,
        correctReply: correctReply.trim(),
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => onFixed(correctReply.trim()),
      },
    );
  }

  return (
    <div className="card-surface p-4 ml-auto max-w-[78%]">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-sans text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">
          <Sparkles size={13} strokeWidth={1.75} className="text-magenta" />
          Cargar respuesta correcta
        </h4>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancelar"
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors"
        >
          <X size={12} strokeWidth={1.75} />
        </button>
      </div>

      {!skills || skills.length === 0 ? (
        <div className="rounded-lg bg-warning-bg border border-warning-border px-3 py-2 text-xs font-sans text-warning-text">
          Este cliente no tiene un manual creado todavía. Creá uno desde
          /skills antes de cargar casos validados.
        </div>
      ) : (
        <>
          {skills.length > 1 && (
            <div className="mb-3">
              <label className="block font-sans text-2xs uppercase tracking-wider text-text-muted mb-1">
                Manual de destino
              </label>
              <select
                value={selectedSkillId}
                onChange={(e) => setSelectedSkillId(e.target.value)}
                className="w-full h-8 px-2 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
              >
                {skills.map((sk) => (
                  <option key={sk.id} value={sk.id}>
                    {sk.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <textarea
            value={correctReply}
            onChange={(e) => setCorrectReply(e.target.value)}
            rows={3}
            placeholder="Qué debió haber respondido la IA"
            className="w-full px-3 py-2 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none resize-none mb-3"
          />
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas opcionales (contexto, por qué)"
            className="w-full h-8 px-3 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none mb-3"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="font-sans text-2xs text-text-muted">
              Al guardar, se crea una versión nueva del manual con este caso en la
              sección "Casos validados". Después promovés a producción desde
              /skills.
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                !correctReply.trim() || !selectedSkillId || append.isPending
              }
              className={cn(
                "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-magenta text-white font-sans text-xs font-medium",
                "hover:bg-magenta-hover transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed shrink-0",
              )}
            >
              <Check size={12} strokeWidth={2} />
              {append.isPending ? "Guardando…" : "Guardar al manual"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SessionItem({
  session,
  isActive,
  onActivate,
  onDelete,
  onRename,
}: {
  session: {
    id: string;
    title: string;
    platform: string;
    turnsCount: number;
    updatedAt: string;
    createdByEmail: string | null;
  };
  isActive: boolean;
  onActivate: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(session.title);
      // Esperar al mount del input antes de focus.
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [editing, session.title]);

  function commit() {
    const next = draft.trim();
    if (next && next !== session.title) {
      onRename(next.slice(0, 200));
    }
    setEditing(false);
  }

  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onActivate}
        className={cn(
          "w-full text-left rounded-lg px-3 py-2 transition-colors",
          isActive
            ? "bg-surface-3 text-text-primary"
            : "text-text-secondary hover:bg-surface-2 hover:text-text-primary",
        )}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            onBlur={commit}
            className="w-full bg-surface-2 border border-magenta rounded-md px-2 py-1 font-sans text-sm font-medium text-text-primary outline-none"
          />
        ) : (
          <div className="font-sans text-sm font-medium truncate pr-12">
            {session.title}
          </div>
        )}
        <div className="flex items-center gap-2 mt-0.5 font-mono text-2xs text-text-muted">
          <span>{session.platform}</span>
          <span>·</span>
          <span>{session.turnsCount} turnos</span>
        </div>
        <div className="font-mono text-2xs text-text-disabled mt-0.5">
          {new Date(session.updatedAt).toLocaleDateString("es-AR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
          {session.createdByEmail ? ` · ${session.createdByEmail.split("@")[0]}` : ""}
        </div>
      </button>
      {!editing && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            title="Renombrar sesión"
            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-2"
          >
            <Pencil size={11} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Eliminar sesión"
            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-error-text hover:bg-error-bg"
          >
            <Trash2 size={11} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </li>
  );
}
